import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import { MAX_OBJECTS_PER_REQUEST } from '@ctw/shared/api/fqs/client';
import {
  type UsePatientQueryFnContext,
  type UsePatientQueryOptions,
  usePatientQuery,
} from '@ctw/shared/context/patient-provider';
import type { CustomQueryFnContext } from '@ctw/shared/hooks/use-ctw-query';
import type { TypedDocumentString } from '@ctw/shared/types/graphql/__generated__/graphql';
import { get } from 'lodash-es';
import { useMemo } from 'react';

export type InferQueryResult<T> = T extends TypedDocumentString<infer TResult, never>
  ? TResult
  : never;

export interface UseGraphqlFetchAllPatientResourcesOptions<
  TResourceType extends ResourceTypeString,
  TQuery extends TypedDocumentString<object, object>,
  TQueryResult extends InferQueryResult<TQuery> = InferQueryResult<TQuery>,
  TContext extends CustomQueryFnContext = CustomQueryFnContext,
> extends Omit<UsePatientQueryOptions<Array<TQueryResult>, TContext>, 'queryFn'> {
  resourceType: TResourceType;
  query: TQuery;
  variables: object;
  requestHeaders?: Record<string, string>;
}

export function useGraphqlFetchAllPatientResources<
  TResourceType extends ResourceTypeString,
  // biome-ignore lint/suspicious/noExplicitAny: Necessary here to allow any query type
  TQuery extends TypedDocumentString<any, any>,
  TQueryResult extends InferQueryResult<TQuery> = InferQueryResult<TQuery>,
  TContext extends CustomQueryFnContext = CustomQueryFnContext,
>({
  requestHeaders,
  ...queryOptions
}: UseGraphqlFetchAllPatientResourcesOptions<TResourceType, TQuery, TQueryResult, TContext>) {
  const useQueryOptionsWithContextualQueryKeys: UsePatientQueryOptions<
    Array<TQueryResult>,
    TContext
    // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify for by-value deps
  > = useMemo(
    () => ({
      ...queryOptions,
      queryMeta: { resourceType: queryOptions.resourceType },
      // Prefix the query ID with `patient.`
      queryId: `gqlFetchAll.patient.${queryOptions.resourceType}.${queryOptions.queryId}`,
      // Add the patient's UPID to the query key so we never cache across patients
      queryKey: [...(queryOptions.queryKey ?? []), queryOptions.resourceType],
      // Add the patient into the query function context
      queryFn: async (context: UsePatientQueryFnContext<TContext>) => {
        const connectionName = `${queryOptions.resourceType}Connection`;
        let cursor = '';
        let hasNextPage = false;
        let pageCount = 0;
        const responses: Array<TQueryResult> = [];
        do {
          const response = await context.graphqlClient.request<TQueryResult>(
            queryOptions.query.toString(),
            {
              upid: context.patient.UPID,
              first: MAX_OBJECTS_PER_REQUEST,
              ...queryOptions.variables,
              cursor,
            },
            requestHeaders,
          );
          cursor = get(response, [connectionName, 'pageInfo', 'endCursor'], '');
          hasNextPage = [true, 'true'].includes(
            get(response, [connectionName, 'pageInfo', 'hasNextPage'], false),
          );
          responses.push(response);
          pageCount += 1;
        } while (hasNextPage && cursor !== '');

        context.setScopedContextProperty('fetchAllQueryMetrics', {
          resourceType: queryOptions.resourceType,
          pageCount,
          hasNextPage,
        });

        return responses;
      },
    }),
    [JSON.stringify(requestHeaders), JSON.stringify(queryOptions)],
  );

  return usePatientQuery(useQueryOptionsWithContextualQueryKeys);
}
